/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Dropdown from "@react/react-spectrum/Dropdown";
import Button from "@react/react-spectrum/Button";
import { Menu, MenuItem, MenuHeading } from "@react/react-spectrum/Menu";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import withToolTip from "dc-preview-utils/src/HOC/withToolTip";
import { TOOLTIP } from "dc-preview-utils/src/Constants/constants";

import ReadAloudCss from "./readAloud.css";

const ReadAloudPicker = props => (
  <React.Fragment className={ReadAloudCss[props.dropdownClassName]}>
    <ReadAloudPickerDropdownWithTooltip
      {...props}
      tooltip={{
        text: props.intl.formatMessage({ id: props.tooltip }),
        placement: TOOLTIP.PLACEMENT.BOTTOM
      }}
    />
  </React.Fragment>
);

ReadAloudPicker.defaultProps = {
  options: [],
  dropdownClassName: "",
  menuStyle: {},
  menuHeading: "",
  selectHandler: () => {},
  tooltip: "",
  icon: () => {},
  intl: {}
};

ReadAloudPicker.propTypes = {
  options: PropTypes.array,
  dropdownClassName: PropTypes.string,
  menuStyle: PropTypes.object,
  menuHeading: PropTypes.string,
  selectHandler: PropTypes.func,
  tooltip: PropTypes.string,
  icon: PropTypes.func,
  intl: PropTypes.object
};

const ReadAloudPickerDropdown = ({ options, menuStyle, menuHeading, selectHandler, arialabel, icon, intl }) => {
  const menuItems = options.map(({ id, selected, label }) => (
    <MenuItem key={id} value={id} selected={selected} className={ReadAloudCss.menuItem} data-testid={label}>
      {label}
    </MenuItem>
  ));
  const menuPopoverStyle = {
    ...menuStyle,
    transform: "translateX(calc(16px - 50%))",
    marginTop: "8px"
  };
  const useLabelIcon = typeof icon === "string";
  return (
    <Dropdown onSelect={selectHandler}>
      <Button
        dropdownTrigger
        label={useLabelIcon && icon}
        icon={!useLabelIcon && <CustomIcon url={icon} />}
        variant="action"
        aria-label={intl.formatMessage({ id: arialabel })}
        quiet
      />
      <Menu dropdownMenu style={menuPopoverStyle} className={ReadAloudCss.pickerMenu}>
        <MenuHeading>{menuHeading && intl.formatMessage({ id: menuHeading })}</MenuHeading>
        {menuItems}
      </Menu>
    </Dropdown>
  );
};
const ReadAloudPickerDropdownWithTooltip = withToolTip(ReadAloudPickerDropdown);

const ReadAloudPickerWithIntl = injectIntl(ReadAloudPicker);
export { ReadAloudPickerWithIntl as ReadAloudPicker };
